import { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/blog/Header';
import Footer from '../components/blog/Footer';
import PostCard from '../components/blog/PostCard';
import { Container } from '../components/ui/Container';
import Pagination from '../components/ui/Pagination';
import Spinner from '../components/ui/Spinner';
import { usePagination } from '../hooks/usePagination';
import SEO from '../components/seo/SEO';
import StructuredData, { generateCategorySchema, generateBreadcrumbSchema } from '../components/seo/StructuredData';
import CrawlerSeoContent from '../components/seo/CrawlerSeoContent';
import { fetchCategoryBySlug, fetchCategoryArticles } from '../store/actions/categoryActions';
import { transformCategory, transformArticles } from '../utils/transformData';
import { isMongoObjectId } from '../utils/slugUtils';
import { useRegionalSeo } from '../hooks/useRegionalSeo';
import { useSeoExtras } from '../context/SiteSeoContext';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const Main = styled.main`
  ${Container} {
    padding-top: 2rem;
    padding-bottom: 2rem;
    
    @media (min-width: 768px) {
      padding-top: 3rem;
      padding-bottom: 3rem;
    }
    
    @media (min-width: 1024px) {
      padding-top: 4rem;
      padding-bottom: 4rem;
    }
  }
`;

const CategoryHeader = styled.div`
  margin-bottom: 2.5rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.25rem;
  border-radius: ${props => props.theme.borderRadius['2xl'] || '1.25rem'};
  background: linear-gradient(
    135deg,
    ${props => props.theme.colors.card} 0%,
    ${props => props.theme.colors.secondary} 100%
  );
  border: 1px solid ${props => props.theme.colors.border};
  
  @media (min-width: 768px) {
    margin-bottom: 3.25rem;
    gap: 2rem;
    padding: 1.75rem 2rem;
  }

  @media (min-width: 1024px) {
    gap: 2.25rem;
    padding: 2rem 2.25rem;
  }
`;

const CategoryImageBox = styled.div`
  width: 7rem;
  height: 7rem;
  border-radius: 1.25rem;
  overflow: hidden;
  flex-shrink: 0;
  background: ${props => props.theme.colors.background};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    0 10px 28px ${props => props.theme.colors.foreground}14,
    0 0 0 3px ${props => props.theme.colors.card};

  @media (min-width: 768px) {
    width: 9rem;
    height: 9rem;
    border-radius: 1.5rem;
  }

  @media (min-width: 1024px) {
    width: 10.5rem;
    height: 10.5rem;
    border-radius: 1.75rem;
  }

  @media (min-width: 1440px) {
    width: 11.5rem;
    height: 11.5rem;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CategoryImagePlaceholder = styled.span`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 2.25rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  color: ${props => props.theme.colors.mutedForeground};
  line-height: 1;

  @media (min-width: 768px) {
    font-size: 2.75rem;
  }

  @media (min-width: 1024px) {
    font-size: 3.25rem;
  }
`;

const CategoryHeaderText = styled.div`
  min-width: 0;
  flex: 1;
`;

const CategoryTitle = styled.h1`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.875rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin: 0 0 0.5rem;
  letter-spacing: -0.02em;
  line-height: 1.15;
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 0.65rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 3rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 3.25rem;
  }
`;

const CategoryDescription = styled.p`
  font-size: 0.975rem;
  color: ${props => props.theme.colors.mutedForeground};
  max-width: 40rem;
  line-height: 1.65;
  margin: 0;
  
  @media (min-width: 768px) {
    font-size: 1.125rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1.2rem;
  }
`;

const PostsCount = styled.p`
  display: inline-flex;
  align-items: center;
  margin-top: 0.85rem;
  padding: 0.3rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${props => props.theme.colors.foreground};
  background: ${props => props.theme.colors.card};
  border: 1px solid ${props => props.theme.colors.border};
  
  @media (min-width: 768px) {
    margin-top: 1rem;
    font-size: 0.875rem;
  }

  @media (min-width: 1024px) {
    font-size: 0.9375rem;
  }
`;

const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 4rem;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.75rem;
  }
  
  @media (min-width: 768px) {
    gap: 2rem;
    margin-bottom: 5rem;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 2rem;
    margin-bottom: 6rem;
  }
  
  @media (min-width: 1440px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 2.5rem;
  }
  
  @media (min-width: 1920px) {
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 3rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 0;
  
  p {
    color: ${props => props.theme.colors.mutedForeground};
    font-size: 1rem;
    
    @media (min-width: 1024px) {
      font-size: 1.125rem;
    }
  }
`;

const Category = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { slug: slugParam } = useParams();
  const { language, apiRegion, getCategoryUrl } = useLanguage();
  const regionalSeo = useRegionalSeo(`/category/${slugParam || ''}`);
  const categoriesRegionalSeo = useRegionalSeo('/categories');
  const seoExtras = useSeoExtras();
  const [currentPage] = useState(1);
  
  // Use language from context (set based on region from URL)
  const activeLanguage = language;

  const { category: categoryFromStore, categoryArticles, loading: categoryLoading } = useSelector(state => state.category);

  useEffect(() => {
    if (slugParam) {
      dispatch(fetchCategoryBySlug(slugParam, activeLanguage, apiRegion));
      dispatch(fetchCategoryArticles(slugParam, { 
        page: currentPage, 
        limit: 10,
        lang: activeLanguage,
        region: apiRegion
      }));
    }
  }, [dispatch, slugParam, currentPage, activeLanguage, apiRegion]);

  const category = categoryFromStore ? transformCategory(categoryFromStore) : null;
  const categoryPosts = transformArticles(categoryArticles?.data || categoryArticles || []);

  const { paginatedItems, totalPages, goToPage } = usePagination(categoryPosts, 6);
  
  if (categoryLoading) {
    return (
      <PageWrapper>
        <Header />
        <Main>
          <Container>
            <Spinner />
          </Container>
        </Main>
        <Footer />
      </PageWrapper>
    );
  }
  
  if (!category) {
    return (
      <PageWrapper>
        <SEO noindex title="Category Not Found" description="The category you're looking for doesn't exist." />
        <Header />
        <Main>
          <Container>
            <EmptyState>
              <p>Category not found.</p>
            </EmptyState>
          </Container>
        </Main>
        <Footer />
      </PageWrapper>
    );
  }

  if (category?.slug && slugParam && isMongoObjectId(slugParam) && category.slug !== slugParam) {
    return <Navigate to={getCategoryUrl(category.slug)} replace />;
  }

  const categoryDescriptions = {
    'Technology': 'Explore the latest in technology, AI, software development, and digital innovation.',
    'Design': 'Discover design trends, techniques, and inspiration for creating beautiful user experiences.',
    'Lifestyle': 'Tips, stories, and insights for living a balanced and fulfilling life.',
    'Business': 'Business strategies, entrepreneurship, and insights for professional growth.',
    'Wellness': 'Health, wellness, and mindfulness practices for a better you.',
    'Travel': 'Travel guides, destination stories, and adventure inspiration.'
  };

  const categoryUrl = getCategoryUrl(category.slug || slugParam);
  const homeUrl = regionalSeo.alternateUrls[activeLanguage] || '/';
  const categoriesUrl = categoriesRegionalSeo.pageUrl;
  
  const breadcrumbItems = [
    { name: 'Home', url: homeUrl },
    { name: 'Categories', url: categoriesUrl },
    { name: category.name, url: categoryUrl }
  ];

  return (
    <PageWrapper>
      <SEO
        strict={Boolean(category.seo?.metaTitle)}
        title={category.seo?.metaTitle || category.name}
        description={category.seo?.metaDescription || category.description}
        url={categoryUrl}
        language={regionalSeo.language}
        availableLanguages={regionalSeo.availableLanguages}
        alternateUrls={regionalSeo.alternateUrls}
        {...seoExtras}
      />
      <StructuredData
        data={generateCategorySchema(category, activeLanguage, categoryPosts.length)}
      />
      <StructuredData
        data={generateBreadcrumbSchema(breadcrumbItems)}
      />
      <CrawlerSeoContent
        title={category.name}
        intro={category.description || categoryDescriptions[category.name]}
      />
      <Header />
      <Main>
        <Container>
          <CategoryHeader>
            <CategoryImageBox>
              {category.imageUrl ? (
                <img src={category.imageUrl} alt="" />
              ) : (
                <CategoryImagePlaceholder>
                  {(category.name || 'C').charAt(0).toUpperCase()}
                </CategoryImagePlaceholder>
              )}
            </CategoryImageBox>
            <CategoryHeaderText>
              <CategoryTitle>{category.name}</CategoryTitle>
              <CategoryDescription>
                {category.description || categoryDescriptions[category.name] || `Explore articles in the ${category.name} category.`}
              </CategoryDescription>
              <PostsCount>
                {t('category.articlesCount', { count: categoryPosts.length })}
              </PostsCount>
            </CategoryHeaderText>
          </CategoryHeader>

          {categoryPosts.length > 0 ? (
            <>
              <PostsGrid>
                {paginatedItems.map((post, index) => (
                  <PostCard
                    key={post._id || `${post.title}-${index}`}
                    {...post}
                    index={index}
                  />
                ))}
              </PostsGrid>
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={goToPage}
                />
              )}
            </>
          ) : (
            <EmptyState>
              <p>No articles found in this category.</p>
            </EmptyState>
          )}
        </Container>
      </Main>
      <Footer />
    </PageWrapper>
  );
};

export default Category;
